export type CommonViewSurfaceId =
  | "demand"
  | "supply"
  | "forecast-tracking"
  | "inventory-health"
  | "supply-walk"
  | "po-inbox";

export type CommonViewFilterV1 = Readonly<{
  field: string;
  operator: string;
  values: readonly string[];
}>;

/**
 * Bounded UI selection metadata for one rendered view.
 *
 * This context is descriptive only. It is never evidence and must never be
 * used to authorize a tenant, run, receipt, primitive, or data access.
 */
export type CommonViewContextV1 = Readonly<{
  version: "CommonViewContext.v1";
  trust: "untrusted_ui_selection";
  surfaceId: CommonViewSurfaceId;
  measure: string | null;
  dateHierarchy: string | null;
  dateRange: Readonly<{
    min: string | null;
    max: string | null;
  }>;
  segments: readonly string[];
  filters: readonly CommonViewFilterV1[];
  postFilters: readonly CommonViewFilterV1[];
}>;

type CommonViewContextInput = Readonly<{
  surfaceId: unknown;
  measure: unknown;
  dateHierarchy: unknown;
  minDate: unknown;
  maxDate: unknown;
  segments: unknown;
  filters: unknown;
  postFilters: unknown;
}>;

const SURFACES = new Set<CommonViewSurfaceId>([
  "demand",
  "supply",
  "forecast-tracking",
  "inventory-health",
  "supply-walk",
  "po-inbox",
]);
const MAX_SEGMENTS = 32;
const MAX_FILTERS = 64;
const MAX_FILTER_VALUES = 256;
const MAX_VIEW_CONTEXT_BYTES = 16 * 1_024;

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes: number): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= maxBytes &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const nullableText = (
  value: unknown,
  maxBytes: number,
): string | null | undefined =>
  value === null || value === undefined ? null : boundedText(value, maxBytes);

const canonicalDate = (value: unknown): string | null | undefined => {
  if (value === null || value === undefined) return null;
  if (!(value instanceof Date) || !Number.isFinite(value.getTime())) {
    return undefined;
  }
  return value.toISOString();
};

const stringList = (
  value: unknown,
  maximumItems: number,
  maximumItemBytes: number,
): readonly string[] | undefined => {
  if (!Array.isArray(value) || value.length > maximumItems) return undefined;
  const result: string[] = [];
  for (const candidate of value) {
    const item = boundedText(candidate, maximumItemBytes);
    if (!item) return undefined;
    result.push(item);
  }
  return Object.freeze(result);
};

const viewFilters = (
  value: unknown,
): readonly CommonViewFilterV1[] | undefined => {
  if (value === undefined) return Object.freeze([]);
  if (!Array.isArray(value) || value.length > MAX_FILTERS) return undefined;
  const filters: CommonViewFilterV1[] = [];
  for (const candidate of value) {
    if (!Array.isArray(candidate) || candidate.length !== 3) return undefined;
    const field = boundedText(candidate[0], 128);
    const operator = boundedText(candidate[1], 64);
    const values = stringList(candidate[2], MAX_FILTER_VALUES, 512);
    if (!field || !operator || !values) return undefined;
    filters.push(Object.freeze({ field, operator, values }));
  }
  return Object.freeze(filters);
};

export function createCommonViewContext(
  input: CommonViewContextInput,
): CommonViewContextV1 | undefined {
  if (!SURFACES.has(input.surfaceId as CommonViewSurfaceId)) return undefined;
  const measure = nullableText(input.measure, 128);
  const dateHierarchy = nullableText(input.dateHierarchy, 128);
  const min = canonicalDate(input.minDate);
  const max = canonicalDate(input.maxDate);
  const segments =
    input.segments === undefined
      ? Object.freeze([])
      : stringList(input.segments, MAX_SEGMENTS, 128);
  const filters = viewFilters(input.filters);
  const postFilters = viewFilters(input.postFilters);
  if (
    measure === undefined ||
    dateHierarchy === undefined ||
    min === undefined ||
    max === undefined ||
    !segments ||
    !filters ||
    !postFilters
  ) {
    return undefined;
  }
  const aggregateFilterValues = [...filters, ...postFilters].reduce(
    (total, filter) => total + filter.values.length,
    0,
  );
  if (
    aggregateFilterValues > MAX_FILTER_VALUES ||
    (min !== null && max !== null && min > max)
  ) {
    return undefined;
  }

  const context = Object.freeze({
    version: "CommonViewContext.v1" as const,
    trust: "untrusted_ui_selection" as const,
    surfaceId: input.surfaceId as CommonViewSurfaceId,
    measure,
    dateHierarchy,
    dateRange: Object.freeze({ min, max }),
    segments,
    filters,
    postFilters,
  });
  return utf8ByteLength(JSON.stringify(context)) <= MAX_VIEW_CONTEXT_BYTES
    ? context
    : undefined;
}
